import { useIsMobile } from "@/hooks/use-mobile.tsx";
import { Button } from "@/components/ui/button.tsx";
import { cn } from "@/lib/utils.ts";
import { X } from "lucide-react";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

const ALL_DAY_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"] as const;
const ALL_DAY_LABELS: Record<DayKey, string> = {
  sun: "Su",
  mon: "M",
  tue: "Tu",
  wed: "W",
  thu: "Th",
  fri: "F",
  sat: "Sa",
};

type DayKey = (typeof ALL_DAY_KEYS)[number];
type TimeSlot = { startTime: string; endTime: string };
type PreferredDays = Partial<Record<DayKey, TimeSlot[]>>;

// Re-exported from shared/client so native's summaries can't drift; existing
// importers keep this path.
export { summarizeAvailabilityDays } from "@/shared/meetingPreferenceSummary.ts";

/** Rotate ALL_DAY_KEYS so that `startDay` comes first */
function getDayOrder(startDay: DayKey): DayKey[] {
  const idx = ALL_DAY_KEYS.indexOf(startDay);
  return [...ALL_DAY_KEYS.slice(idx), ...ALL_DAY_KEYS.slice(0, idx)];
}

interface WeeklyTimePickerProps {
  value: PreferredDays;
  onChange: (days: PreferredDays) => void;
  slotDuration?: number; // minutes per slot, default 30
  startHour?: number; // default 7
  endHour?: number; // default 21
  startDay?: DayKey; // first day of the week, default "sun"
  cellHeight?: string; // custom cell height class, default "h-3"
  helpText?: string; // optional help text below the grid
  onClose?: () => void; // dismiss callback for mobile full-screen overlay
}

function slotIndexToTime(index: number, slotDuration: number, startHour: number): string {
  const totalMinutes = startHour * 60 + index * slotDuration;
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

function timeToSlotIndex(time: string, slotDuration: number, startHour: number): number {
  const [h, m] = time.split(":").map(Number);
  const totalMinutes = h * 60 + m - startHour * 60;
  return Math.round(totalMinutes / slotDuration);
}

/** Convert PreferredDays time slots into a Set of cell keys like "mon-4" */
function timeSlotsToSelectedCells(
  days: PreferredDays,
  slotDuration: number,
  startHour: number,
  totalSlots: number,
  dayKeys: readonly DayKey[],
): Set<string> {
  const cells = new Set<string>();
  for (const day of dayKeys) {
    const slots = days[day];
    if (!slots) continue;
    for (const slot of slots) {
      const startIdx = timeToSlotIndex(slot.startTime, slotDuration, startHour);
      const endIdx = timeToSlotIndex(slot.endTime, slotDuration, startHour);
      for (let i = Math.max(0, startIdx); i < Math.min(endIdx, totalSlots); i++) {
        cells.add(`${day}-${i}`);
      }
    }
  }
  return cells;
}

/** Convert selected cells back to PreferredDays time slots */
function selectedCellsToTimeSlots(
  cells: Set<string>,
  slotDuration: number,
  startHour: number,
  dayKeys: readonly DayKey[],
): PreferredDays {
  const result: PreferredDays = {};
  for (const day of dayKeys) {
    const indices: number[] = [];
    for (const cell of cells) {
      if (cell.startsWith(`${day}-`)) {
        indices.push(Number(cell.split("-")[1]));
      }
    }
    if (indices.length === 0) continue;

    indices.sort((a, b) => a - b);

    // Group contiguous indices into time slots
    const slots: TimeSlot[] = [];
    let rangeStart = indices[0];
    let rangeEnd = indices[0];

    for (let i = 1; i < indices.length; i++) {
      if (indices[i] === rangeEnd + 1) {
        rangeEnd = indices[i];
      } else {
        slots.push({
          startTime: slotIndexToTime(rangeStart, slotDuration, startHour),
          endTime: slotIndexToTime(rangeEnd + 1, slotDuration, startHour),
        });
        rangeStart = indices[i];
        rangeEnd = indices[i];
      }
    }
    slots.push({
      startTime: slotIndexToTime(rangeStart, slotDuration, startHour),
      endTime: slotIndexToTime(rangeEnd + 1, slotDuration, startHour),
    });

    result[day] = slots;
  }
  return result;
}

function formatHour(hour: number): string {
  if (hour === 0) return "12 AM";
  if (hour < 12) return `${hour} AM`;
  if (hour === 12) return "12 PM";
  return `${hour - 12} PM`;
}

/** Short clock label for minutes-from-midnight: 540 → "9", 780 → "1", 990 → "4:30". */
export function shortClock(totalMinutes: number): string {
  const h24 = Math.floor(totalMinutes / 60) % 24;
  const m = totalMinutes % 60;
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return m === 0 ? String(h12) : `${h12}:${String(m).padStart(2, "0")}`;
}

/** Width of the leading time-label column; kept in sync with `gridTemplateColumns`. */
const LABEL_COL_WIDTH = 48;

/**
 * Drag state for the picker.
 * - "create" starts on an empty cell and paints a new rectangular range. It spans
 *   both rows (slots) and columns (days) between the start and current pointer
 *   position, so a single drag can select the same time across multiple days.
 * - "resize" starts on an already-selected cell. The pivot is the FAR edge of the
 *   original range, so the near edge follows the pointer — that gives you both grow
 *   and shrink from any click position. Resize stays within its day.
 */
type DragState =
  | {
      mode: "create";
      startDayIdx: number;
      currentDayIdx: number;
      startSlot: number;
      currentSlot: number;
    }
  | {
      mode: "resize";
      dayIdx: number;
      pivot: number;
      originalStart: number;
      originalEnd: number;
      currentSlot: number;
      hasMoved: boolean;
    };

/** Walk left/right from `slot` to find the contiguous selected range it belongs to. */
function findRangeContaining(
  cells: Set<string>,
  day: DayKey,
  slot: number,
  totalSlots: number,
): { start: number; end: number } | null {
  if (!cells.has(`${day}-${slot}`)) return null;
  let start = slot;
  while (start > 0 && cells.has(`${day}-${start - 1}`)) start--;
  let end = slot;
  while (end < totalSlots - 1 && cells.has(`${day}-${end + 1}`)) end++;
  return { start, end };
}

/** Pivot at the FAR edge so the near edge follows the pointer. Ties default to `end`. */
function computeResizePivot(range: { start: number; end: number }, clickSlot: number): number {
  const distToStart = clickSlot - range.start;
  const distToEnd = range.end - clickSlot;
  return distToEnd >= distToStart ? range.end : range.start;
}

/**
 * Resolve a resize gesture to its new range, or null if the user dragged the
 * near edge past the pivot — that collapses the range and removes it.
 * Single-slot ranges never collapse; remove those via double-tap.
 */
function resolveResizedRange(
  pivot: number,
  originalStart: number,
  originalEnd: number,
  currentSlot: number,
): { start: number; end: number } | null {
  if (originalStart !== originalEnd) {
    const pastPivot =
      (pivot === originalEnd && currentSlot > pivot) ||
      (pivot === originalStart && currentSlot < pivot);
    if (pastPivot) return null;
  }
  return { start: Math.min(pivot, currentSlot), end: Math.max(pivot, currentSlot) };
}

/**
 * All cell keys in the rectangle spanned by two (day, slot) corners, inclusive.
 * Corners may be given in any order. A "create" drag paints this rectangle, so a
 * single gesture can span multiple days (columns) as well as multiple slots.
 */
export function cellsInRect(
  dayKeys: readonly DayKey[],
  startDayIdx: number,
  endDayIdx: number,
  startSlot: number,
  endSlot: number,
): Set<string> {
  const minDay = Math.min(startDayIdx, endDayIdx);
  const maxDay = Math.max(startDayIdx, endDayIdx);
  const minSlot = Math.min(startSlot, endSlot);
  const maxSlot = Math.max(startSlot, endSlot);
  const cells = new Set<string>();
  for (let d = minDay; d <= maxDay; d++) {
    for (let i = minSlot; i <= maxSlot; i++) cells.add(`${dayKeys[d]}-${i}`);
  }
  return cells;
}

type BlockPosition = "none" | "solo" | "first" | "middle" | "last";

type BlockInfo = {
  position: BlockPosition;
  /** Time range label, only set on the cell where it should be displayed */
  label?: string;
};

/**
 * Compute contiguous block metadata for every cell in the grid.
 * Returns a Map keyed by "day-slotIdx".
 */
function computeBlockInfo(
  effectiveSelected: Set<string>,
  dayKeys: readonly DayKey[],
  totalSlots: number,
  slotDuration: number,
  startHour: number,
): Map<string, BlockInfo> {
  const info = new Map<string, BlockInfo>();

  for (const day of dayKeys) {
    // Collect selected slot indices for this day
    const indices: number[] = [];
    for (let i = 0; i < totalSlots; i++) {
      if (effectiveSelected.has(`${day}-${i}`)) {
        indices.push(i);
      }
    }

    // Group into contiguous ranges
    const ranges: { start: number; end: number }[] = [];
    let i = 0;
    while (i < indices.length) {
      const rangeStart = indices[i];
      let rangeEnd = rangeStart;
      while (i + 1 < indices.length && indices[i + 1] === rangeEnd + 1) {
        i++;
        rangeEnd = indices[i];
      }
      ranges.push({ start: rangeStart, end: rangeEnd });
      i++;
    }

    // Mark each cell with its block position
    for (const range of ranges) {
      const blockLen = range.end - range.start + 1;
      const startMinutes = startHour * 60 + range.start * slotDuration;
      const endMinutes = startHour * 60 + (range.end + 1) * slotDuration;
      const label = `${shortClock(startMinutes)}-${shortClock(endMinutes)}`;
      // Place label on the first cell of the range
      const labelIdx = range.start;

      for (let s = range.start; s <= range.end; s++) {
        let position: BlockPosition;
        if (blockLen === 1) {
          position = "solo";
        } else if (s === range.start) {
          position = "first";
        } else if (s === range.end) {
          position = "last";
        } else {
          position = "middle";
        }
        info.set(`${day}-${s}`, {
          position,
          label: s === labelIdx ? label : undefined,
        });
      }
    }
  }

  return info;
}

export const WeeklyTimePicker = ({
  value,
  onChange,
  slotDuration = 30,
  startHour = 6,
  endHour = 23,
  startDay = "sun",
  cellHeight = "h-3",
  helpText,
  onClose,
}: WeeklyTimePickerProps) => {
  const isMobile = useIsMobile();
  const dayKeys = useMemo(() => getDayOrder(startDay), [startDay]);
  const dayLabels = useMemo(() => dayKeys.map((k) => ALL_DAY_LABELS[k]), [dayKeys]);
  const totalSlots = ((endHour - startHour) * 60) / slotDuration;

  const [selected, setSelected] = useState<Set<string>>(() =>
    timeSlotsToSelectedCells(value, slotDuration, startHour, totalSlots, dayKeys),
  );

  const dragState = useRef<DragState | null>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const gridContainerRef = useRef<HTMLDivElement>(null);
  const [dragPreview, setDragPreview] = useState<{ add: Set<string>; remove: Set<string> }>(() => ({
    add: new Set(),
    remove: new Set(),
  }));

  // Double-click detection (since e.preventDefault() on pointerdown suppresses native dblclick)
  const lastPointerDown = useRef<{ dayIdx: number; slotIdx: number; time: number } | null>(null);

  // Dynamic cell height for mobile full-screen mode
  const [dynamicCellHeight, setDynamicCellHeight] = useState<number | null>(null);

  useEffect(() => {
    if (!isMobile) {
      setDynamicCellHeight(null);
      return;
    }
    const container = gridContainerRef.current;
    if (!container) return;
    const measure = () => {
      const available = container.clientHeight;
      // +2 for header row and trailing label row
      const rows = totalSlots + 2;
      setDynamicCellHeight(Math.floor(available / rows));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
  }, [isMobile, totalSlots]);

  const timeLabels = useMemo(() => {
    const labels: string[] = [];
    for (let i = 0; i < totalSlots; i++) {
      const totalMinutes = startHour * 60 + i * slotDuration;
      const minute = totalMinutes % 60;
      if (minute === 0) {
        labels.push(formatHour(Math.floor(totalMinutes / 60)));
      } else {
        labels.push("");
      }
    }
    return labels;
  }, [totalSlots, startHour, slotDuration]);

  // Compute effective selection (selected with drag preview applied)
  const effectiveSelected = useMemo(() => {
    if (dragPreview.add.size === 0 && dragPreview.remove.size === 0) return selected;
    const eff = new Set(selected);
    for (const cell of dragPreview.remove) eff.delete(cell);
    for (const cell of dragPreview.add) eff.add(cell);
    return eff;
  }, [selected, dragPreview]);

  // Compute block metadata for rendering
  const blockInfo = useMemo(
    () => computeBlockInfo(effectiveSelected, dayKeys, totalSlots, slotDuration, startHour),
    [effectiveSelected, dayKeys, totalSlots, slotDuration, startHour],
  );

  const commitSelection = useCallback(
    (newSelected: Set<string>) => {
      setSelected(newSelected);
      onChange(selectedCellsToTimeSlots(newSelected, slotDuration, startHour, dayKeys));
    },
    [onChange, slotDuration, startHour, dayKeys],
  );

  /** Given a clientY, compute which slot row the pointer is over */
  const getSlotFromY = useCallback(
    (clientY: number): number => {
      const grid = gridRef.current;
      if (!grid) return 0;
      const rect = grid.getBoundingClientRect();
      const headerHeight = rect.height / (totalSlots + 1);
      const y = clientY - rect.top - headerHeight;
      const slot = Math.floor((y / (rect.height - headerHeight)) * totalSlots);
      return Math.max(0, Math.min(totalSlots - 1, slot));
    },
    [totalSlots],
  );

  /** Given a clientX, compute which day column the pointer is over */
  const getDayFromX = useCallback(
    (clientX: number): number => {
      const grid = gridRef.current;
      if (!grid) return 0;
      const rect = grid.getBoundingClientRect();
      const colWidth = (rect.width - LABEL_COL_WIDTH) / dayKeys.length;
      const dayIdx = Math.floor((clientX - rect.left - LABEL_COL_WIDTH) / colWidth);
      return Math.max(0, Math.min(dayKeys.length - 1, dayIdx));
    },
    [dayKeys.length],
  );

  const handleDoubleClick = useCallback(
    (dayIdx: number) => {
      const day = dayKeys[dayIdx];
      const newSelected = new Set(selected);
      for (let i = 0; i < totalSlots; i++) {
        newSelected.delete(`${day}-${i}`);
      }
      commitSelection(newSelected);
    },
    [dayKeys, selected, totalSlots, commitSelection],
  );

  const handlePointerDown = useCallback(
    (dayIdx: number, slotIdx: number, e: React.PointerEvent) => {
      e.preventDefault();

      const day = dayKeys[dayIdx];
      const now = Date.now();
      const last = lastPointerDown.current;
      if (last && last.dayIdx === dayIdx && last.slotIdx === slotIdx && now - last.time < 400) {
        lastPointerDown.current = null;
        const range = findRangeContaining(selected, day, slotIdx, totalSlots);
        if (range) {
          // Double-tap on a selected cell: remove just that range.
          const newSelected = new Set(selected);
          for (let i = range.start; i <= range.end; i++) newSelected.delete(`${day}-${i}`);
          commitSelection(newSelected);
        } else {
          // Double-tap on empty cell: clear the entire day.
          handleDoubleClick(dayIdx);
        }
        return;
      }
      lastPointerDown.current = { dayIdx, slotIdx, time: now };

      // Capture the pointer so move/up keep firing even when the pointer leaves
      // the grid mid-drag. Without this, releasing outside the container skips
      // pointerup and the drag state stays stuck.
      e.currentTarget.setPointerCapture(e.pointerId);

      const range = findRangeContaining(selected, day, slotIdx, totalSlots);
      if (range) {
        dragState.current = {
          mode: "resize",
          dayIdx,
          pivot: computeResizePivot(range, slotIdx),
          originalStart: range.start,
          originalEnd: range.end,
          currentSlot: slotIdx,
          hasMoved: false,
        };
        // No preview yet — a release without movement is a no-op.
        setDragPreview({ add: new Set(), remove: new Set() });
      } else {
        dragState.current = {
          mode: "create",
          startDayIdx: dayIdx,
          currentDayIdx: dayIdx,
          startSlot: slotIdx,
          currentSlot: slotIdx,
        };
        setDragPreview({ add: new Set([`${day}-${slotIdx}`]), remove: new Set() });
      }
    },
    [selected, dayKeys, handleDoubleClick, totalSlots, commitSelection],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      const state = dragState.current;
      if (!state) return;
      e.preventDefault();

      const currentSlot = getSlotFromY(e.clientY);
      state.currentSlot = currentSlot;

      if (state.mode === "create") {
        const currentDayIdx = getDayFromX(e.clientX);
        state.currentDayIdx = currentDayIdx;
        const add = cellsInRect(
          dayKeys,
          state.startDayIdx,
          currentDayIdx,
          state.startSlot,
          currentSlot,
        );
        setDragPreview({ add, remove: new Set() });
        return;
      }

      const day = dayKeys[state.dayIdx];
      const newRange = resolveResizedRange(
        state.pivot,
        state.originalStart,
        state.originalEnd,
        currentSlot,
      );
      const unchanged =
        newRange !== null &&
        newRange.start === state.originalStart &&
        newRange.end === state.originalEnd;
      if (!unchanged) state.hasMoved = true;

      const add = new Set<string>();
      if (newRange) {
        for (let i = newRange.start; i <= newRange.end; i++) add.add(`${day}-${i}`);
      }
      const remove = new Set<string>();
      for (let i = state.originalStart; i <= state.originalEnd; i++) {
        const key = `${day}-${i}`;
        if (!add.has(key)) remove.add(key);
      }
      setDragPreview({ add, remove });
    },
    [getSlotFromY, getDayFromX, dayKeys],
  );

  const handlePointerUp = useCallback(() => {
    const state = dragState.current;
    if (!state) return;

    dragState.current = null;
    setDragPreview({ add: new Set(), remove: new Set() });

    if (state.mode === "create") {
      const newSelected = new Set(selected);
      for (const cell of cellsInRect(
        dayKeys,
        state.startDayIdx,
        state.currentDayIdx,
        state.startSlot,
        state.currentSlot,
      )) {
        newSelected.add(cell);
      }
      commitSelection(newSelected);
      return;
    }

    if (!state.hasMoved) return; // click on selected cell without drag = no-op

    const day = dayKeys[state.dayIdx];

    const newRange = resolveResizedRange(
      state.pivot,
      state.originalStart,
      state.originalEnd,
      state.currentSlot,
    );
    const newSelected = new Set(selected);
    for (let i = state.originalStart; i <= state.originalEnd; i++) {
      newSelected.delete(`${day}-${i}`);
    }
    if (newRange) {
      for (let i = newRange.start; i <= newRange.end; i++) {
        newSelected.add(`${day}-${i}`);
      }
    }
    commitSelection(newSelected);
  }, [selected, dayKeys, commitSelection]);

  const displayHelpText = isMobile
    ? "Drag empty to add. Drag a block to resize, or past its edge to remove."
    : helpText;

  const grid = (
    <div
      ref={gridRef}
      className="grid gap-0"
      style={{
        gridTemplateColumns: `${LABEL_COL_WIDTH}px repeat(${dayKeys.length}, 1fr)`,
      }}
    >
      {/* Header row */}
      <div />
      {dayLabels.map((label) => (
        <div
          key={label}
          className="text-center text-xs font-medium text-muted-foreground py-1"
          style={dynamicCellHeight != null ? { height: dynamicCellHeight } : undefined}
        >
          {label}
        </div>
      ))}

      {/* Time rows */}
      {Array.from({ length: totalSlots }, (_, slotIdx) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: slot index is the stable identity for grid rows
        <Fragment key={slotIdx}>
          {/* Time label */}
          <div
            className="text-right text-[10px] text-muted-foreground pr-2 -mt-1.5"
            style={dynamicCellHeight != null ? { height: dynamicCellHeight } : undefined}
          >
            {timeLabels[slotIdx]}
          </div>

          {/* Day cells */}
          {dayKeys.map((day, dayIdx) => {
            const cellKey = `${day}-${slotIdx}`;
            const block = blockInfo.get(cellKey);
            const isSelected = block !== undefined;
            const isOnHour = (startHour * 60 + slotIdx * slotDuration) % 60 === 0;

            return (
              <div
                key={day}
                className={cn(
                  "transition-colors duration-75 relative",
                  isSelected ? "cursor-ns-resize" : "cursor-pointer",
                  dynamicCellHeight == null && cellHeight,
                  // Grid lines for unselected cells
                  !isSelected && "border-r border-border/40",
                  !isSelected && isOnHour && "border-t border-t-border/60",
                  !isSelected && !isOnHour && "border-t border-t-border/20",
                  !isSelected && "hover:bg-muted-foreground/10 hover:rounded-lg",
                  dayIdx === 0 && !isSelected && "border-l border-l-border/40",
                )}
                style={dynamicCellHeight != null ? { height: dynamicCellHeight } : undefined}
                onPointerDown={(e) => handlePointerDown(dayIdx, slotIdx, e)}
              >
                {isSelected && block && (
                  <div
                    className={cn(
                      "absolute inset-x-0.5 bg-primary flex items-center justify-center z-10",
                      block.position === "solo" && "inset-y-0 rounded-lg",
                      block.position === "first" && "top-0 -bottom-px rounded-t-lg",
                      block.position === "middle" && "-top-px -bottom-px",
                      block.position === "last" && "-top-px bottom-0 rounded-b-lg",
                    )}
                  >
                    {!isMobile && block.label && (
                      <span className="text-[11px] font-semibold text-primary-foreground leading-none">
                        {block.label}
                      </span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </Fragment>
      ))}

      {/* Trailing end-hour label */}
      <div
        className="text-right text-[10px] text-muted-foreground pr-2 -mt-1.5"
        style={dynamicCellHeight != null ? { height: dynamicCellHeight } : undefined}
      >
        {formatHour(endHour)}
      </div>
      {dayKeys.map((day) => (
        <div key={day} className="border-t border-t-border/60" />
      ))}
    </div>
  );

  // Mobile: full-screen overlay via portal
  if (isMobile) {
    return createPortal(
      <div className="fixed inset-0 z-50 bg-background flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b shrink-0">
          <h2 className="text-base font-semibold">Set availability</h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="rounded-full"
            data-statsig-name="availability-picker-mobile-close"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Grid fills remaining space */}
        <div
          ref={gridContainerRef}
          className="flex-1 min-h-0 px-2 py-2 select-none"
          style={{ touchAction: "none" }}
          onPointerUp={handlePointerUp}
          onPointerMove={handlePointerMove}
        >
          {grid}
        </div>

        {/* Footer help text */}
        {displayHelpText && (
          <div className="px-4 py-2 border-t shrink-0">
            <p className="text-sm text-muted-foreground text-center">{displayHelpText}</p>
          </div>
        )}
      </div>,
      document.body,
    );
  }

  // Desktop: inline grid (unchanged)
  return (
    <div className="select-none" onPointerUp={handlePointerUp} onPointerMove={handlePointerMove}>
      {grid}
      {helpText && <p className="text-sm text-muted-foreground mt-3 text-center">{helpText}</p>}
    </div>
  );
};
